import './Link.scss';
import * as React from 'react';
import classNames from 'classnames';
import { Link as GatsbyLink } from 'gatsby';
import { AccentColors } from 'types/accentColors.js';
import { ExternalLink } from '@/react-ui';
export type Props = {
  to: string;
  children: React.ReactNode;
  newWindow?: boolean;
  external?: boolean;
  download?: boolean;
  title?: string;
  'data-testid'?: string;
} & (
  | {
      // To help ensure style consistency, most links should use these props to
      // specify their appearance, rather than specifying className directly.
      button?: boolean;
      color?: AccentColors | 'on-dark';
      block?: boolean;
      extraClassName?: string;

      // Specifying `className` will override button/color/block +
      // extraClassName, so we want to throw a type error if `className` is used
      // in combination those. Only specify className when a link should NOT
      // have ANY of the usual style rules applied to it.
      className?: never;
    }
  | {
      className?: string;
      button?: never;
      color?: never;
      block?: never;
      extraClassName?: never;
    }
);

/**
 * A custom styled link component. Uses the Gatsby `Link` component so should
 * only be used for internal site links.
 *
 * May be styled either as a text link, or as a button by passing the `button`
 * prop.
 */
export default class Link extends React.Component<Props> {
  static defaultProps = {
    button: false,
    block: false,
    newWindow: false,
    external: false,
    download: false,
  };
  render() {
    const {
      to,
      title,
      button,
      children,
      color,
      block,
      external,
      download,
      newWindow,
      className: classNameFromProps,
      extraClassName,
    } = this.props;

    const dataTestId = this.props['data-testid'];

    const className =
      classNameFromProps != null
        ? classNameFromProps
        : classNames(
            {
              [`Link--${color || ''}`]: color != null && !button,
              [`Link--button-${color || ''}`]: color != null && button,
              Link: !button,
              'Link--button': button,
              'Link--block': block,
            },
            extraClassName,
          );

    /*
    Only links to other parts of this gatsby site should use GatsbyLink.

    We should use an ExternalLink (which bypasses Gatsby's prefetching and chunking stuff) for links that:
        - take the user to some place that isn't this gatsby site
        - open a new window
        - start a download
     */
    if (external || newWindow || download) {
      return (
        <ExternalLink
          className={className}
          href={to}
          newWindow={newWindow}
          title={title}
          data-testid={dataTestId}
        >
          {children}
        </ExternalLink>
      );
    }

    return (
      <GatsbyLink
        className={className}
        to={to}
        title={title}
        data-testid={dataTestId}
      >
        {children}
      </GatsbyLink>
    );
  }
}
